import { toSafeNumber } from '../state/timelineModel';

/**
 * One effect item -> one DSL call.
 *
 * The argument order here has to match the param_spec each function is
 * registered with in Backend/Common/AI/video_edit_script.py. Keywords are used
 * throughout rather than positional arguments so an added or reordered
 * parameter cannot silently shift every value one place along.
 */

// The DSL has no string escapes, so a quote inside a value would end it.
const str = (value) => `"${String(value ?? '').replace(/["\\\n\r]/g, ' ').trim()}"`;

const num = (value, fallback) => {
    const parsed = toSafeNumber(value);
    const resolved = parsed === null ? fallback : parsed;
    return Number.isInteger(resolved) ? String(resolved) : Number(resolved).toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
};

const bool = (value) => (value ? 'true' : 'false');

/** type -> [dsl function, (params) => argument list]. */
const EMITTERS = {
    text: (p) => ['text', [
        `text=${str(p.text)}`,
        `x=${num(p.x, 0.5)}`,
        `y=${num(p.y, 0.5)}`,
        `fontFrac=${num(p.fontFrac, 0.06)}`,
        `color=${str(p.color || '#ffffff')}`,
        `anchor=${str(p.anchor || 'center')}`,
        ...(p.fontFamily ? [`fontFamily=${str(p.fontFamily)}`] : []),
        `shadow=${bool(p.shadow)}`,
        `box=${bool(p.box)}`,
    ]],

    box: (p) => ['box', [
        `x=${num(p.x, 0.25)}`,
        `y=${num(p.y, 0.25)}`,
        `w=${num(p.w, 0.5)}`,
        `h=${num(p.h, 0.3)}`,
        `color=${str(p.color || '#d9363e')}`,
        `filled=${bool(p.filled)}`,
        `thicknessFrac=${num(p.thicknessFrac, 0.006)}`,
    ]],

    crop: (p) => ['crop_frame', [
        `x=${num(p.x, 0.1)}`,
        `y=${num(p.y, 0.1)}`,
        `w=${num(p.w, 0.8)}`,
        `h=${num(p.h, 0.8)}`,
    ]],

    zoom: (p) => ['zoom', [
        `zoom=${num(p.zoom, 1.5)}`,
        `x=${num(p.x, 0.5)}`,
        `y=${num(p.y, 0.5)}`,
    ]],

    sticker: (p) => ['sticker', [
        `overlayAsset=${str(p.overlayAsset)}`,
        `x=${num(p.x, 0.5)}`,
        `y=${num(p.y, 0.5)}`,
        `scale=${num(p.scale, 0.25)}`,
    ]],

    overlay: (p) => ['overlay_card', [
        `overlayAsset=${str(p.overlayAsset)}`,
        `x=${num(p.x, 0.5)}`,
        `y=${num(p.y, 0.35)}`,
        `scale=${num(p.scale, 0.6)}`,
        ...(p.label ? [`label=${str(p.label)}`] : []),
        `animate=${str(p.animate === false || p.animate === 'none' ? 'none' : (p.animate === true ? 'bottom' : p.animate))}`,
        `animDuration=${num(p.animDuration, 0.4)}`,
    ]],

    fade: (p) => ['fade_fx', [
        `fadeType=${str(p.fadeType || 'in')}`,
        `duration=${num(p.duration, 1)}`,
    ]],

    flip: (p) => ['flip', [
        `direction=${str(p.direction || 'horizontal')}`,
    ]],

    look: (p) => ['look', [
        `warmth=${num(p.warmth, 0)}`,
        `saturation=${num(p.saturation, 1)}`,
        `brightness=${num(p.brightness, 0)}`,
        `grayscale=${bool(p.grayscale)}`,
    ]],
};

/** Effect types this editor can render into a script. */
export const canEmitEffect = (effect) => (
    Boolean(effect?.type) && !effect.readOnly && Object.hasOwn(EMITTERS, effect.type)
);

/**
 * Emit `outVar = fn(inVar, …)` for one effect.
 *
 * `startTime`/`endTime` are relative to the clip, not the timeline: the effect
 * is applied to the clip's own stream before it is concatenated, so a clip that
 * later moves takes its effects with it.
 */
export const emitEffect = (effect, inVar, outVar, clip) => {
    const build = EMITTERS[effect.type];
    if (!build) return null;

    const [fn, args] = build(effect.params || {});

    const clipStart = clip ? clip.start : 0;
    const start = Math.max(0, (toSafeNumber(effect.start) ?? clipStart) - clipStart);
    const end = toSafeNumber(effect.end);
    const timing = [`start=${num(start, 0)}s`];
    if (end !== null && end > (effect.start ?? clipStart)) {
        timing.push(`end=${num(Math.max(0, end - clipStart), 0)}s`);
    }

    return `${outVar} = ${fn}(${inVar}, ${[...args, ...timing].join(', ')})`;
};
